import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Plus, Wand2, Activity, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui'
import { useSimulations } from '@/hooks/useSimulations'
import { useCapital } from '@/hooks/useCapital'

export function SimulationsPage() {
  const { businessId } = useParams<{ businessId: string }>()
  const navigate = useNavigate()
  const { simulations, isLoading, error, generateScenarios } = useSimulations(businessId)
  const { data: capitalData } = useCapital(businessId)
  
  const [isGenerating, setIsGenerating] = useState(false)

  const handleGenerateScenarios = async () => {
    if (!capitalData) return alert('Data modal belum siap')
    setIsGenerating(true)
    
    // Default base assumptions for auto-generate
    const payload = {
      baseName: `Auto Scenario ${new Date().toISOString().split('T')[0]}`,
      initialCapital: capitalData.settings.initialCapital,
      initialCash: capitalData.metrics.paidCapital,
      initialEquity: capitalData.settings.initialCapital, // simplified
      affordableLossPercentage: capitalData.settings.affordableLossPercentage,
      durationMonths: 12,
      baseMonthlyRevenue: 10000000, // IDR 10M default base
      fixedOperatingExpenseMonthly: 3000000, // IDR 3M default fixed
      taxRateOfRevenue: 0.01 // 1% tax
    }

    const res = await generateScenarios(payload)
    setIsGenerating(false)
    if (!res.success) {
      alert(res.error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p>Memuat daftar simulasi...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Simulasi Keuangan</h1>
          <p className="text-sm text-slate-400 mt-1">Lakukan proyeksi laba-rugi dan arus kas ke depan.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleGenerateScenarios} variant="outline" disabled={isGenerating || !capitalData} className="gap-2">
            <Wand2 size={16} />
            {isGenerating ? 'Menghasilkan...' : 'Auto-Generate (3 Skenario)'}
          </Button>
          <Button onClick={() => navigate(`/businesses/${businessId}/simulations/new`)} className="gap-2">
            <Plus size={16} />
            Buat Manual
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
          <p>Gagal memuat: {error}</p>
        </div>
      )}

      {simulations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 px-4 text-center border border-dashed border-slate-700 rounded-xl bg-slate-800/20">
          <div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center mb-4 text-slate-500">
            <Activity size={32} />
          </div>
          <h3 className="text-lg font-semibold text-slate-200">Belum ada simulasi</h3>
          <p className="text-sm text-slate-400 mt-2 max-w-sm mb-6">
            Uji kelayakan ide atau target bulan depan sebelum mengeluarkan uang sungguhan.
          </p>
          <Button onClick={() => navigate(`/businesses/${businessId}/simulations/new`)} variant="outline">
            Mulai Simulasi Manual
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {simulations.map(sim => (
            <Link
              key={sim.id}
              to={`/businesses/${businessId}/simulations/${sim.id}`}
              className="block p-5 rounded-xl border border-slate-800 bg-slate-900 hover:border-brand-500/50 hover:bg-slate-800/50 transition-all group"
            >
              <div className="flex justify-between items-start mb-3">
                <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider
                  ${sim.type === 'optimistic' ? 'bg-emerald-500/10 text-emerald-400' :
                    sim.type === 'conservative' ? 'bg-rose-500/10 text-rose-400' :
                    sim.type === 'moderate' ? 'bg-blue-500/10 text-blue-400' :
                    'bg-slate-700 text-slate-300'}
                `}>
                  {sim.type}
                </span>
                <span className="text-xs text-slate-500">{sim.durationMonths} Bulan</span>
              </div>
              <h3 className="text-lg font-bold text-slate-100 mb-1">{sim.name}</h3>
              <p className="text-xs text-slate-400 line-clamp-2 min-h-[32px]">{sim.notes || 'Manual Simulation'}</p>
              
              <div className="flex items-center justify-between text-xs font-medium text-slate-400 group-hover:text-brand-400 transition-colors pt-4 mt-4 border-t border-slate-800/50">
                <span>Lihat Detail</span>
                <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
